import moment from 'moment';
import { API_DATE_FORMAT, PTO_TYPES } from '@ubeya/shared/constants';
import exportExcel from './exportExcel';
import {
  formatSettingValue,
  getHolidayFieldLabel,
  getSettingLabel,
  getSettingListChange
} from './settings-audit-labels';

const DEPRECATED_TEXT = ', Invalid date';

const formatAuditDate = (value, format) => moment.utc(new Date(value)).format(format);

const SETTINGS_ITEM_AUDIT_LOG_TEXT = {
  AccountProfileUpdated: 'accountProfileUpdatedAuditLog',
  EventTemplateCreated: 'eventTemplateCreatedAuditLog',
  EventTemplateUpdated: 'eventTemplateUpdatedAuditLog',
  EventTemplateDeleted: 'eventTemplateDeletedAuditLog',
  DepartmentCreated: 'departmentCreatedAuditLog',
  DepartmentUpdated: 'departmentUpdatedAuditLog',
  DepartmentDeleted: 'departmentDeletedAuditLog',
  LeaveTypeCreated: 'leaveTypeCreatedAuditLog',
  LeaveTypeUpdated: 'leaveTypeUpdatedAuditLog',
  LeaveTypeDeleted: 'leaveTypeDeletedAuditLog',
  PositionCreated: 'positionCreatedAuditLog',
  PositionUpdated: 'positionUpdatedAuditLog',
  PositionDeleted: 'positionDeletedAuditLog'
};

const HOLIDAY_AUDIT_LOG_TEXT = {
  added: 'holidayAddedAuditLog',
  removed: 'holidayRemovedAuditLog',
  updated: 'holidayUpdatedAuditLog'
};

const TRANSLATED_ITEM_NAME_AUDIT_LOG_TYPES = new Set(['PositionCreated', 'PositionUpdated', 'PositionDeleted']);

const SETTINGS_CHANGE_AUDIT_LOG_TYPES = new Set([
  'AccountSettingUpdated',
  'AccountPayrollSettingUpdated',
  'AccountCRMSettingUpdated',
  'BranchSettingUpdated',
  'BranchPayrollSettingUpdated'
]);

export const SETTINGS_AUDIT_LOG_TYPES = [
  ...SETTINGS_CHANGE_AUDIT_LOG_TYPES,
  ...Object.keys(SETTINGS_ITEM_AUDIT_LOG_TEXT)
];

const CAPTAIN_AUDIT_DISPLAY_FALLBACK = ' ';

function captainAuditLabel(value) {
  if (typeof value !== 'string') {
    return CAPTAIN_AUDIT_DISPLAY_FALLBACK;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return CAPTAIN_AUDIT_DISPLAY_FALLBACK;
  }
  return trimmed;
}

const FIELD_SLUGS = {
  // Fields that their slug is different than the localization key
  PHONE_SECONDARY: 'phoneSecondary',
  EMPLOYMENT_TYPE_ID: 'employmentTypeId',
  DEPARTMENT_ID: 'departmentId',
  IS_PENDING: 'isPending',
  IS_DAILY: 'isDaily',
  IS_SHIFT: 'isShift'
};

// Fields that their slug is different than the localization key
const FIELD_NAMES_BY_SLUG = {
  [FIELD_SLUGS.PHONE_SECONDARY]: 'secondaryPhone',
  [FIELD_SLUGS.EMPLOYMENT_TYPE_ID]: 'employmentType',
  [FIELD_SLUGS.DEPARTMENT_ID]: 'department',
  [FIELD_SLUGS.IS_PENDING]: 'pendingApproval',
  [FIELD_SLUGS.IS_DAILY]: 'daily',
  [FIELD_SLUGS.IS_SHIFT]: 'shift'
};

const PTO_LABELS = {
  [PTO_TYPES.Holiday]: 'vacation',
  [PTO_TYPES.SickDay]: 'sick'
};

// Employee-reported → `timesheetUser<Clock>By<Suffix>AuditLog`.
const EMPLOYEE_CLOCK_SOURCE_SUFFIX = {
  crew_app: 'ByEmployee',
  kiosk_qr: 'ByQR',
  kiosk_employee_id: 'ByKioskID'
};

// Admin-reported → `timesheet<Prefix><Clock>AuditLog`.
const ADMIN_CLOCK_SOURCE_PREFIX = {
  admin_web: 'AdminWeb',
  admin_os: 'AdminWeb',
  admin_job_service: 'AdminWeb',
  admin_mobile: 'AdminBusinessApp',
  admin_qr: 'AdminQR',
  admin_set_as_schedule: 'AdminSetAsSchedule'
};

// Captain-reported → `timesheet<Prefix><Clock>AuditLog`.
const CAPTAIN_CLOCK_SOURCE_PREFIX = {
  captain_manual: 'CaptainManual',
  captain_qr: 'CaptainQR',
  captain_set_as_schedule: 'CaptainSetAsSchedule'
};

/**
 * Slug for the clock-method-aware message, or null to keep the existing source-less wording.
 */
const timesheetClockSourceSlug = ({ clockSource, reportedType, clockLabel, hasShift }) => {
  if (!clockSource) {
    return null;
  }

  const scope = hasShift ? '' : 'Job';

  if (reportedType === 'admin') {
    const prefix = ADMIN_CLOCK_SOURCE_PREFIX[clockSource];
    return prefix ? `timesheet${scope}${prefix}${clockLabel}AuditLog` : null;
  }

  const suffix = EMPLOYEE_CLOCK_SOURCE_SUFFIX[clockSource];
  return suffix ? `timesheet${scope}User${clockLabel}${suffix}AuditLog` : null;
};

const captainClockSourceSlug = ({ clockSource, clockLabel }) => {
  const prefix = clockSource && CAPTAIN_CLOCK_SOURCE_PREFIX[clockSource];
  return prefix ? `timesheet${prefix}${clockLabel}AuditLog` : null;
};

const getProfileFieldName = (t, profileFieldSlug) => {
  if (profileFieldSlug?.includes('payrollFields')) {
    return profileFieldSlug
      .split('.')
      .map((slug) => t(slug))
      .join(' - ');
  }

  if (Object.keys(FIELD_NAMES_BY_SLUG).includes(profileFieldSlug)) return t(FIELD_NAMES_BY_SLUG[profileFieldSlug]);

  return t(profileFieldSlug);
};

const formatShiftDetails = ({ shift, shortDateMonthFormat, timeFormat }) => {
  const { shiftDate, shiftStartTime, shiftEndTime, shiftPosition, shiftLocation } = shift;

  // dana 13:00-14:00, area
  const details = [];

  if (shiftDate) details.push(moment(shiftDate, API_DATE_FORMAT).format(shortDateMonthFormat));
  if (shiftPosition) details.push(shiftPosition);
  if (shiftStartTime) details.push(moment.utc(shiftStartTime).format(timeFormat));
  if (shiftEndTime) details.push(`-${moment.utc(shiftEndTime).format(timeFormat)}`);
  if (shiftLocation) details.push(`, ${shiftLocation}`);

  return details.join('');
};

// e.g. " 15:00-15:30" — only when the start is known (open breaks just show the start).
const formatBreakRange = ({ startRaw, endRaw, timeFormat }) => {
  const start = startRaw && moment.utc(startRaw).format(timeFormat);
  const end = endRaw && moment.utc(endRaw).format(timeFormat);
  return start ? ` ${start}${end ? `-${end}` : ''}` : '';
};

// Rendered only when a duration is available, so open/unbounded breaks don't show an empty "( min)".
const formatDurationLabel = (duration) => (duration ? ` (${duration} min)` : '');

const WEEKLY_PREFERENCE_TEXT_BY_ACTION = {
  set: 'audit_log_EmployeeWeeklyPreferencesSet',
  deleted: 'audit_log_EmployeeWeeklyPreferencesDeleted'
};

export const generateAuditLogBody = ({
  t,
  type,
  data,
  timeFormat = 'HH:mm',
  shortDateMonthFormat = 'DD/MM',
  dateFormat = 'DD/MM/YYYY',
  getProfileFieldValue,
  currency,
  hasSensitiveDataAccess
}) => {
  const reporterTitle = data?.data?.reportedType === 'admin' ? t('Administrator') : t('User');

  const {
    reportedType,
    reporterName,
    clockSource,
    startTime: startTimeRaw,
    endTime: endTimeRaw,
    checkInTime: checkInTimeRaw,
    checkOutTime: checkOutTimeRaw,
    employeeName,
    shiftName,
    shiftDate,
    shiftPosition,
    shiftLocation,
    date,
    hourlyRate,
    effectiveDate,
    adminName,
    jobDate,
    positionName,
    projectName,
    projectDate,
    projectLocation,
    captainName,
    positionSlug,
    locationName,
    eventName: captainEventName,
    dateRange,
    areaName,
    areaNames,
    taskName,
    taskNames,
    shiftStartTime,
    shiftEndTime,
    area: captainAreaName,
    value,
    message,
    initiatingAccountName,
    initiatingAdminName,
    employeeAccountName,
    slug,
    srcProjectDate,
    deactivateReason,
    isFile,
    isPhoto,
    fileChange,
    fieldType,
    fieldName,
    isSensitive,
    prevValue,
    newValue,
    prevShift,
    newShift,
    branchName,
    isPaused,
    isUpdatedAudience,
    isAutoPause,
    contractName,
    oldContractName,
    newContractName,
    leaveName,
    affectedEmployeeCount,
    contractType,
    amount,
    ptoType,
    ptoValue,
    ptoHours,
    startDate,
    endDate,
    bulkRequestsAmount,
    leaveCount,
    payoutRemainder,
    resetAmount,
    leavesPayedOut,
    totalAmount,
    unit,
    balanceAtTermination,
    recordCount,
    holidayName,
    holidayDate,
    breakDuration,
    breakStartTime,
    breakEndTime,
    ruleName,
    scaleType,
    threshold,
    previousHourlyRate,
    newHourlyRate,
    action,
    userReportedStartTime: userReportedStartTimeRaw,
    scope,
    settingName,
    settingGroup,
    isValueHidden,
    itemAction,
    itemDate,
    itemField,
    itemName
  } = data.data || {};

  const startTime = startTimeRaw && moment.utc(startTimeRaw).format(timeFormat);
  const endTime = endTimeRaw && moment.utc(endTimeRaw).format(timeFormat);
  const checkInTime = checkInTimeRaw && moment.utc(checkInTimeRaw).format(timeFormat);
  const checkOutTime = checkOutTimeRaw && moment.utc(checkOutTimeRaw).format(timeFormat);
  const userReportedStartTime = userReportedStartTimeRaw && moment.utc(userReportedStartTimeRaw).format(timeFormat);

  if (SETTINGS_CHANGE_AUDIT_LOG_TYPES.has(type) && itemAction) {
    const valueOptions = { settingName: itemField, settingGroup, dateFormat, timeFormat };

    return {
      text: HOLIDAY_AUDIT_LOG_TEXT[itemAction],
      values: {
        adminName,
        itemName,
        itemDate: itemDate ? moment(itemDate, API_DATE_FORMAT).format(dateFormat) : '',
        settingName: getHolidayFieldLabel(t, itemField),
        prevValue: formatSettingValue(t, prevValue, valueOptions),
        newValue: formatSettingValue(t, newValue, valueOptions)
      }
    };
  }

  if (SETTINGS_CHANGE_AUDIT_LOG_TYPES.has(type)) {
    const valueOptions = { settingName, settingGroup, dateFormat, timeFormat };
    const values = {
      adminName,
      branchName,
      settingName: getSettingLabel(t, settingName, scope, settingGroup),
      prevValue: formatSettingValue(t, prevValue, valueOptions),
      newValue: formatSettingValue(t, newValue, valueOptions)
    };

    if (isValueHidden) {
      return { values, text: scope === 'branch' ? 'branchSettingChangedAuditLog' : 'accountSettingChangedAuditLog' };
    }

    const listChange = getSettingListChange(t, prevValue, newValue, valueOptions);

    if (listChange) {
      const scopePrefix = scope === 'branch' ? 'branch' : 'account';
      const { addedValue, removedValue } = listChange;
      const changeSuffix = (addedValue && removedValue && 'AddedRemoved') || (addedValue && 'Added') || 'Removed';

      return {
        values: { ...values, addedValue, removedValue },
        text: `${scopePrefix}SettingList${changeSuffix}AuditLog`
      };
    }

    return { values, text: scope === 'branch' ? 'branchSettingUpdatedAuditLog' : 'accountSettingUpdatedAuditLog' };
  }

  if (SETTINGS_ITEM_AUDIT_LOG_TEXT[type]) {
    return {
      text: SETTINGS_ITEM_AUDIT_LOG_TEXT[type],
      values: {
        adminName,
        branchName,
        itemName: TRANSLATED_ITEM_NAME_AUDIT_LOG_TYPES.has(type) && itemName ? t(itemName) : itemName
      }
    };
  }

  let text;
  let values = {};

  switch (type) {
    case 'BreakDeleted': {
      const breakTimeRange = formatBreakRange({ startRaw: breakStartTime, endRaw: breakEndTime, timeFormat });
      // Shift label = position + location (position is translated from its slug); falls back to whichever exists.
      const breakShiftName = [positionSlug ? t(positionSlug) : '', locationName].filter(Boolean).join(', ');

      const breakAuditValues = {
        adminName: reporterName,
        reporterTitle,
        employeeName: employeeName || reporterName,
        shiftName: breakShiftName,
        shiftDate: shiftDate && moment(shiftDate, API_DATE_FORMAT).format(shortDateMonthFormat),
        date: date && moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
        breakTimeRange,
        breakDurationLabel: formatDurationLabel(breakDuration)
      };

      text = t(shiftDate ? 'breakDeletedAuditLog' : 'breakJobDeletedAuditLog', breakAuditValues).replace(
        DEPRECATED_TEXT,
        ''
      );
      break;
    }

    case 'TimesheetTaskFieldUpdated': {
      // Show "empty" only for unset/cleared values — keep legitimate falsy values (0, false, "No").
      const withEmptyFallback = (val) => (val === null || val === undefined || val === '' ? t('empty') : val);

      values = {
        adminName,
        fieldName,
        prevValue: withEmptyFallback(prevValue),
        newValue: withEmptyFallback(newValue)
      };
      text = 'timesheetTaskFieldUpdatedAuditLog';
      break;
    }

    case 'UpdatePayrollField': {
      const slugTrans = t(slug);

      text = t('updatePayrollFieldAuditLog', {
        adminName,
        slugTrans,
        value,
        employeeName: employeeName || reporterName,
        shiftName,
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat)
      }).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'ResetTimesheetTime': {
      text = t(shiftDate ? 'timesheetResetTimeCreateAuditLog' : 'timesheetJobResetTimeCreateAuditLog', {
        adminName: reporterName,
        reporterTitle,
        employeeName: employeeName || reporterName,
        shiftName,
        date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat)
      }).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'CreateTimesheetStartTime': {
      const defaultStartTimeKey =
        reportedType === 'admin'
          ? shiftDate
            ? 'timesheetStartTimeCreateAuditLog'
            : 'timesheetJobStartTimeCreateAuditLog'
          : 'timesheetUserStartTimeCreateAuditLog';

      const isBusinessAppAligned =
        (clockSource === 'admin_mobile' || clockSource === 'admin_qr') &&
        userReportedStartTimeRaw &&
        startTimeRaw &&
        !moment.utc(userReportedStartTimeRaw).isSame(moment.utc(startTimeRaw), 'minute');

      const startTimeAuditKey = isBusinessAppAligned
        ? 'businessAppAlignedStartTimeAuditLog'
        : timesheetClockSourceSlug({ clockSource, reportedType, clockLabel: 'StartTime', hasShift: !!shiftDate }) ||
          defaultStartTimeKey;

      text = t(startTimeAuditKey, {
        adminName: reporterName,
        reporterTitle,
        employeeName: employeeName || reporterName,
        shiftName,
        date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat),
        startTime,
        actionTime: userReportedStartTime
      }).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'CreateTimesheetEndTime': {
      text = t(
        timesheetClockSourceSlug({ clockSource, reportedType, clockLabel: 'EndTime', hasShift: !!shiftDate }) ||
          (reportedType === 'admin'
            ? shiftDate
              ? 'timesheetEndTimeCreateAuditLog'
              : 'timesheetJobEndTimeCreateAuditLog'
            : 'timesheetUserEndTimeCreateAuditLog'),
        {
          adminName: reporterName,
          reporterTitle,
          employeeName: employeeName || reporterName,
          shiftName,
          date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
          shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat),
          endTime
        }
      ).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'CreateTimesheetCheckInTime': {
      text = t(
        timesheetClockSourceSlug({ clockSource, reportedType, clockLabel: 'CheckInTime', hasShift: !!shiftDate }) ||
          (reportedType === 'admin'
            ? shiftDate
              ? 'timesheetCheckInTimeCreateAuditLog'
              : 'timesheetJobCheckInTimeCreateAuditLog'
            : 'timesheetUserCheckInTimeCreateAuditLog'),
        {
          adminName: reporterName,
          reporterTitle,
          employeeName: employeeName || reporterName,
          shiftName,
          date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
          shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat),
          checkInTime
        }
      ).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'CreateTimesheetCheckOutTime': {
      text = t(
        timesheetClockSourceSlug({ clockSource, reportedType, clockLabel: 'CheckOutTime', hasShift: !!shiftDate }) ||
          (reportedType === 'admin'
            ? shiftDate
              ? 'timesheetCheckOutTimeCreateAuditLog'
              : 'timesheetJobCheckOutTimeCreateAuditLog'
            : 'timesheetUserCheckOutTimeCreateAuditLog'),
        {
          adminName: reporterName,
          reporterTitle,
          employeeName: employeeName || reporterName,
          shiftName,
          date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
          shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat),
          checkOutTime
        }
      ).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'ClientCreateTimesheetStartTime': {
      text = t(shiftDate ? 'clientTimesheetStartTimeCreateAuditLog' : 'clientTimesheetJobStartTimeCreateAuditLog', {
        adminName: reporterName,
        reporterTitle,
        employeeName: employeeName || reporterName,
        shiftName,
        date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat),
        startTime
      }).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'ClientCreateTimesheetEndTime': {
      text = t(shiftDate ? 'clientTimesheetEndTimeCreateAuditLog' : 'clientTimesheetJobEndTimeCreateAuditLog', {
        adminName: reporterName,
        reporterTitle,
        employeeName: employeeName || reporterName,
        shiftName,
        date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat),
        endTime
      }).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'ClientCreateTimesheetCheckInTime': {
      text = t(shiftDate ? 'clientTimesheetCheckInTimeCreateAuditLog' : 'clientTimesheetJobCheckInTimeCreateAuditLog', {
        adminName: reporterName,
        reporterTitle,
        employeeName: employeeName || reporterName,
        shiftName,
        date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat),
        checkInTime
      }).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'ClientCreateTimesheetCheckOutTime': {
      text = t(
        shiftDate ? 'clientTimesheetCheckOutTimeCreateAuditLog' : 'clientTimesheetJobCheckOutTimeCreateAuditLog',
        {
          adminName: reporterName,
          reporterTitle,
          employeeName: employeeName || reporterName,
          shiftName,
          date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
          shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat),
          checkOutTime
        }
      ).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'AutoFillTimesheet': {
      text = t('timesheetAutoFillTimeCreateAuditLog', {
        adminName: reporterName,
        reporterTitle,
        employeeName: employeeName || reporterName,
        shiftName,
        startTime,
        endTime,
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat)
      }).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'AutoClockedOut': {
      text = t('shiftAutoClockedOutCreateAuditLog', {
        employeeName: employeeName,
        shiftName,
        endTime,
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat)
      }).replace(DEPRECATED_TEXT, '');
      break;
    }

    case 'UpdateJobPayroll': {
      values = {
        hourlyRate,
        employeeName,
        adminName,
        jobDate: formatAuditDate(jobDate, shortDateMonthFormat)
      };
      text = 'updateJobPayrollAuditLog';

      break;
    }
    case 'UpdateEmployeeSlotPayroll': {
      values = {
        hourlyRate,
        employeeName,
        shiftName,
        adminName,
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat)
      };
      text = 'updateEmployeeSlotPayrollAuditLog';

      break;
    }

    case 'UpdateShiftPayroll': {
      values = {
        hourlyRate,
        employeeName,
        shiftName,
        adminName,
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat)
      };

      text = 'updateShiftPayrollAuditLog';

      break;
    }

    case 'UpdateEmployeePayroll': {
      values = {
        hourlyRate,
        employeeName,
        shiftName,
        adminName,
        jobDate,
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat),
        // Formatted in UTC: base wage effective dates are calendar days, not moments in time.
        effectiveDate: effectiveDate && moment.utc(effectiveDate).format(dateFormat)
      };
      // Base wages predating the effective-date history have no date to show.
      text = effectiveDate ? 'updateEmployeePayrollWithEffectiveDateAuditLog' : 'updateEmployeePayrollAuditLog';

      break;
    }

    case 'EmployeeBaseWageUpdatedBySystem': {
      const formatRate = (rate) => `${currency ?? ''}${rate}`;

      values = {
        employeeName,
        threshold,
        ruleName: ruleName ?? '',
        previousHourlyRate:
          previousHourlyRate === null || previousHourlyRate === undefined
            ? t('baseWageNoPreviousRateAuditLog')
            : formatRate(previousHourlyRate),
        newHourlyRate: formatRate(newHourlyRate)
      };
      text =
        scaleType === 'EVENTS_COMPLETED'
          ? 'employeeBaseWageUpdatedBySystemEventsCompletedAuditLog'
          : 'employeeBaseWageUpdatedBySystemTenureAuditLog';

      break;
    }

    case 'UpdateEmployeePositionPayroll': {
      values = {
        hourlyRate,
        employeeName,
        shiftName,
        adminName,
        jobDate,
        positionName: t(positionName),
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat)
      };
      text = 'updateEmployeePositionPayrollAuditLog';
      break;
    }

    case 'EmployeeBroadcastForShift': {
      values = {
        shiftName,
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat),
        shiftPosition: shiftPosition ? ` - ${shiftPosition}` : '',
        employeeName
      };
      text = 'employeeBroadcastForShiftAuditLog';

      break;
    }

    case 'ApprovedWorkingHours': {
      values = {
        adminName,
        shiftName,
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat),
        employeeName,
        startTime,
        endTime,
        reporterName
      };
      text = 'approvedWorkingHoursAuditLog';

      break;
    }

    case 'UnApprovedWorkingHours': {
      values = {
        adminName,
        shiftName,
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat),
        employeeName,
        startTime,
        endTime,
        reporterName
      };
      text = 'UnApprovedWorkingHoursAuditLog';

      break;
    }

    // The initiator's own record of rebooking an external employee it had unbooked.
    case 'EmployeeRebookedForShiftByAccount':
    case 'EmployeeBookedForShift': {
      values = {
        adminName,
        shiftName,
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat),
        shiftPosition: shiftPosition ? ` - ${shiftPosition}` : '',
        employeeName,
        initiatingAccountName,
        initiatingAdminName,
        employeeAccountName
      };

      if (type === 'EmployeeRebookedForShiftByAccount') {
        // This account rebooked another account's (a supplier's) employee: name whose employee it was.
        text = 'externalEmployeeRebookedForShiftAuditLog';
      } else {
        // Another account (a client) rebooked this account's employee it had unbooked: no admin of ours did it.
        text = initiatingAccountName ? 'employeeRebookedForShiftByAccountAuditLog' : 'employeeBookedForShiftAuditLog';
      }

      break;
    }

    // The initiator's own record of a cross-account unbook: worded like its own unbook.
    case 'EmployeeUnBookedForShiftByAccount':
    case 'EmployeeUnBookedForShift': {
      values = {
        adminName,
        shiftName,
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat),
        shiftPosition: shiftPosition ? ` - ${shiftPosition}` : '',
        employeeName,
        message: message ? `. ${message}` : '',
        initiatingAccountName,
        initiatingAdminName,
        employeeAccountName
      };

      if (type === 'EmployeeUnBookedForShiftByAccount' && employeeAccountName) {
        // This account unbooked another account's (a supplier's) employee: name whose employee it was.
        text = 'externalEmployeeUnBookedForShiftAuditLog';
      } else {
        // Another account (a client) unbooked this account's employee: no admin of ours did it.
        text = initiatingAccountName ? 'employeeUnBookedForShiftByAccountAuditLog' : 'employeeUnBookedForShiftAuditLog';
      }
      break;
    }

    case 'EmployeeRequestReleaseFromShift': {
      values = {
        shiftName,
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat),
        employeeName
      };

      text = 'EmployeeRequestReleaseFromShiftAuditLog';
      break;
    }

    case 'EmployeeAutoBookedForShift': {
      values = {
        adminName,
        shiftName,
        shiftDate: formatAuditDate(shiftDate, shortDateMonthFormat),
        employeeName
      };
      text = 'employeeAutoBookedForShiftAuditLog';

      break;
    }

    case 'EmployeeLeftCompany': {
      values = { employeeName };
      text = 'employeeLeftCompanyAuditLog';
      break;
    }

    case 'EmployeeCreatedByAPI': {
      values = { employeeName };
      text = 'employeeCreatedByAPIAuditLog';
      break;
    }

    case 'EmployeeDeletedByAPI': {
      values = { employeeName };
      text = 'employeeDeletedByAPIAuditLog';
      break;
    }

    case 'EmployeeSMSInvited': {
      values = { employeeName, adminName };
      text = 'employeeSMSInvitedAuditLog';
      break;
    }

    case 'EmployeePhotoVerified':
    case 'EmployeePhotoUnverified': {
      const { source } = data.data || {};
      const isVerified = type === 'EmployeePhotoVerified';
      values = { employeeName, adminName };

      if (source === 'integration') {
        text = isVerified
          ? 'employeePhotoVerifiedByIntegrationAuditLog'
          : 'employeePhotoUnverifiedByIntegrationAuditLog';
      } else {
        text = isVerified ? 'employeePhotoVerifiedAuditLog' : 'employeePhotoUnverifiedAuditLog';
      }
      break;
    }

    case 'EmployeeChangeShiftAutoLogUseCase': {
      if (prevShift && newShift) {
        values = {
          adminName,
          prevShift: formatShiftDetails({
            shift: prevShift,
            shortDateMonthFormat,
            timeFormat
          }),
          newShift: formatShiftDetails({ shift: newShift, shortDateMonthFormat, timeFormat }),
          employeeName
        };
        text = 'employeeChangedShiftAuditLogNew';
      } else {
        values = {
          adminName,
          shiftDate,
          employeeName
        };
        text = 'employeeChangedShiftAuditLogOld';
      }

      break;
    }

    case 'ShiftCheckAvailabilityAutoLog': {
      const formattedDate = shiftDate && moment.utc(shiftDate).format(shortDateMonthFormat);
      const shiftTime =
        startTime && endTime ? `${formattedDate} ${startTime} - ${endTime}` : `${formattedDate} ${startTime}`;
      const position = shiftPosition ? `${shiftPosition}, ` : '';
      const area = shiftLocation === projectLocation ? '' : `, ${shiftLocation}`;
      const eventName = projectName ? `${projectName}, ` : '';

      values = {
        adminName,
        position,
        shiftTime,
        area,
        projectName: eventName,
        projectLocation,
        branchName
      };
      if (isAutoPause) {
        text = 'autoPauseAvailabilityRecentActions';
        break;
      }
      if (isPaused !== undefined) {
        text = isPaused ? 'shiftCheckAvailabilityPausedAuditLog' : 'shiftCheckAvailabilityResumedAuditLog';
        break;
      }
      if (isUpdatedAudience) {
        text = 'shiftCheckAvailabilityUpdatedAudienceAuditLog';
        break;
      }
      text = 'shiftCheckAvailabilityCreatedAuditLog';
      break;
    }

    case 'AdminUploadedEmployees': {
      values = { adminName };
      text = 'adminUploadedEmployeesAuditLog';
      break;
    }

    case 'AdminUploadedEmployeesJobs': {
      values = { adminName };
      text = 'adminUploadedEmployeesJobsAuditLog';
      break;
    }

    case 'EmployeeDeleted': {
      values = {
        adminName,
        employeeName
      };
      text = 'employeeDeleteAuditLog';
      break;
    }

    case 'EmployeeRestored': {
      values = {
        adminName,
        employeeName
      };
      text = 'employeeRestoredAuditLog';
      break;
    }

    case 'EmployeeCreated': {
      values = {
        adminName,
        employeeName
      };
      text = 'employeeCreateAuditLog';
      break;
    }
    case 'EmployeeUpdated': {
      values = {
        adminName,
        employeeName,
        fieldName: getProfileFieldName(t, fieldName),
        fileChange,
        document: isPhoto ? 'photo' : 'file'
      };

      const isDocument = isFile || isPhoto;
      const hideSensitiveFieldChangeDetail = !!isSensitive && hasSensitiveDataAccess === false;

      if (!isDocument && !hideSensitiveFieldChangeDetail) {
        values.prevValue = getProfileFieldValue?.({
          profileFieldName: fieldName,
          profileFieldType: fieldType,
          profileFieldValue: prevValue
        });

        values.newValue = getProfileFieldValue?.({
          profileFieldName: fieldName,
          profileFieldType: fieldType,
          profileFieldValue: newValue
        });
      }

      if (isDocument) {
        text = 'employeeUploadDocumentAuditLog';
      } else if (fieldName && hideSensitiveFieldChangeDetail) {
        text = 'employeeUpdateSensitiveFieldAuditLog';
      } else if (fieldName) {
        text = 'employeeUpdateFieldAuditLog';
      } else {
        text = 'employeeUpdateAuditLog';
      }
      break;
    }

    case 'EmployeeActivated': {
      values = {
        adminName,
        employeeName
      };
      text = 'employeeActivatedAuditLog';
      break;
    }
    case 'EmployeeDeactivated': {
      values = {
        adminName,
        employeeName,
        deactivateReason
      };
      text = deactivateReason ? 'employeeDeactivatedWithReasonAuditLog' : 'employeeDeactivatedAuditLog';
      break;
    }

    case 'ProjectCreated': {
      values = {
        adminName,
        projectName,
        projectDate: formatAuditDate(projectDate, shortDateMonthFormat),
        srcProjectDate: srcProjectDate ? formatAuditDate(srcProjectDate, shortDateMonthFormat) : undefined
      };
      text = srcProjectDate ? 'projectCreateViaCopyAuditLog' : 'projectCreateAuditLog';
      break;
    }

    case 'ProjectDeleted': {
      const { projectEndDate } = data.data || {};
      const dateFormatted = formatAuditDate(projectDate, shortDateMonthFormat);
      const endDateFormatted = formatAuditDate(projectEndDate, shortDateMonthFormat);
      values = {
        adminName,
        projectName,
        projectLocation,
        projectDate: dateFormatted !== endDateFormatted ? `${dateFormatted} - ${endDateFormatted}` : dateFormatted
      };
      text = 'projectDeletedAuditLog';
      break;
    }

    case 'ProjectCaptainTimesheetStartTimeUpdated': {
      values = {
        captainName,
        employeeName,
        projectName,
        startTime,
        projectDate: formatAuditDate(projectDate, shortDateMonthFormat)
      };
      text =
        captainClockSourceSlug({ clockSource, clockLabel: 'StartTime' }) || 'projectCaptainTimesheetStartTimeUpdated';
      break;
    }
    case 'ProjectCaptainTimesheetEndTimeUpdated': {
      values = {
        captainName,
        endTime,
        employeeName,
        projectName,
        projectDate: formatAuditDate(projectDate, shortDateMonthFormat)
      };
      text = captainClockSourceSlug({ clockSource, clockLabel: 'EndTime' }) || 'projectCaptainTimesheetEndTimeUpdated';

      break;
    }
    case 'ProjectCaptainTimesheetCheckInTimeUpdated': {
      values = {
        captainName,
        checkInTime,
        employeeName,
        projectName,
        startTime: checkInTime,
        projectDate: formatAuditDate(projectDate, shortDateMonthFormat)
      };
      text =
        captainClockSourceSlug({ clockSource, clockLabel: 'CheckInTime' }) ||
        'projectCaptainTimesheetCheckInTimeUpdated';

      break;
    }
    case 'ProjectCaptainTimesheetCheckOutTimeUpdated': {
      values = {
        captainName,
        checkOutTime,
        employeeName,
        projectName,
        // Same legacy {{startTime}} placeholder as the check-in slug above.
        startTime: checkOutTime,
        projectDate: formatAuditDate(projectDate, shortDateMonthFormat)
      };
      text =
        captainClockSourceSlug({ clockSource, clockLabel: 'CheckOutTime' }) ||
        'projectCaptainTimesheetCheckOutTimeUpdated';

      break;
    }
    case 'ProjectInvoiceIssue': {
      values = {
        adminName,
        projectName,
        projectDate: formatAuditDate(projectDate, shortDateMonthFormat)
      };
      text = 'projectInvoiceIssue';
      break;
    }
    case 'ProjectArchived': {
      values = {
        adminName,
        projectName,
        projectDate: formatAuditDate(projectDate, shortDateMonthFormat)
      };
      text = 'projectArchived';

      break;
    }

    case 'ProjectUnArchived': {
      values = {
        adminName,
        projectName,
        projectDate: formatAuditDate(projectDate, shortDateMonthFormat)
      };
      text = 'projectUnArchived';

      break;
    }

    case 'ProjectUpdated': {
      values = {
        adminName,
        projectName,
        projectDate: formatAuditDate(projectDate, shortDateMonthFormat)
      };
      text = 'projectUpdateAuditLog';

      break;
    }

    case 'ShiftUpdated': {
      values = {
        adminName,
        shiftName: positionSlug ? t(positionSlug) : '',
        ...(startTime && { startTime }),
        ...(endTime && { endTime })
      };
      text =
        startTime && endTime
          ? 'shiftUpdateAuditLogTime'
          : startTime
          ? 'shiftUpdateAuditLogStartTime'
          : endTime
          ? 'shiftUpdateAuditLogEndTime'
          : '';

      break;
    }

    case 'ApplicationCreated': {
      values = {
        adminName,
        projectName,
        projectDate: formatAuditDate(projectDate, shortDateMonthFormat)
      };
      text = 'applicationCreateAuditLog';

      break;
    }

    case 'RequestDeclined': {
      values = {
        adminName,
        employeeName
      };
      text = 'requestDeclinedAuditLog';

      break;
    }

    case 'RequestApproved': {
      values = {
        adminName,
        employeeName
      };
      text = 'requestApprovedAuditLog';
      break;
    }

    case 'RequestsApproved': {
      values = {
        adminName,
        amount: bulkRequestsAmount
      };
      text = bulkRequestsAmount === 1 ? 'recentActionBulkApprovedRequest' : 'recentActionBulkApprovedRequests';
      break;
    }

    case 'RequestsDeclined': {
      values = {
        adminName,
        amount: bulkRequestsAmount
      };
      text = bulkRequestsAmount === 1 ? 'recentActionBulkDeclinedRequest' : 'recentActionBulkDeclinedRequests';
      break;
    }

    case 'IsHostAccountActivated': {
      values = {
        adminName
      };
      text = 'IsHostAccountActivated';
      break;
    }

    case 'AccountContractCreated': {
      values = {
        adminName,
        contractName,
        contractType
      };
      text = 'ContractTypes_createdContract_auditLog';
      break;
    }

    case 'AccountContractUpdated': {
      values = {
        adminName,
        contractName,
        contractType
      };
      text = 'ContractTypes_changeContract_auditLog';
      break;
    }
    case 'AccountContractDeleted': {
      values = {
        adminName,
        contractName,
        contractType
      };
      text = 'ContractTypes_deleteContract_auditLog';
      break;
    }
    case 'EmployeeAssignedContract': {
      values = {
        adminName,
        employeeName,
        contractName
      };
      text = 'ContractTypes_assignEmployee_auditLog';
      break;
    }
    case 'EmployeeContractAdjusted': {
      values = {
        adminName,
        employeeName,
        contractName
      };
      text = 'ContractTypes_adjustEmployeeContract_auditLog';
      break;
    }
    case 'EmployeePtoAllowanceAdjusted': {
      values = {
        adminName,
        employeeName,
        ptoType: t(PTO_LABELS[ptoType])
      };
      text = 'ContractTypes_adjustAllowance_auditLog';
      break;
    }
    case 'AdminAddedEmployeePto': {
      const dates =
        amount > 1
          ? `${moment(startDate, API_DATE_FORMAT).format(shortDateMonthFormat)} - ${moment(
              endDate,
              API_DATE_FORMAT
            ).format(shortDateMonthFormat)}`
          : moment(startDate, API_DATE_FORMAT).format(shortDateMonthFormat);

      values = {
        adminName,
        employeeName,
        ptoType: t(PTO_LABELS[ptoType]),
        amount,
        dates
      };
      if (amount > 1) {
        text =
          ptoType === PTO_TYPES.Holiday
            ? 'ContractTypes_adminAddedPTOsHoliday_auditLog'
            : 'ContractTypes_adminAddedPTOsSickday_auditLog';
      } else {
        text = 'ContractTypes_adminAddedPTO_auditLog';
      }
      break;
    }
    case 'AdminDeletedEmployeePto': {
      values = {
        adminName,
        employeeName,
        ptoType: t(PTO_LABELS[ptoType]),
        date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat)
      };
      text = 'ContractTypes_adminDeletedPTO_auditLog';
      break;
    }
    case 'AdminUsedAllPtoBalance': {
      values = {
        adminName,
        employeeName,
        ptoValue: `${currency}${ptoValue || 0}`,
        ptoHours
      };
      text = 'ContractTypes_adminUsedAllPtoBalance_auditLog';
      break;
    }
    case 'AdminApprovedHolidayRequest': {
      const dates =
        amount > 1
          ? `${moment(startDate, API_DATE_FORMAT).format(shortDateMonthFormat)} - ${moment(
              endDate,
              API_DATE_FORMAT
            ).format(shortDateMonthFormat)}`
          : moment(startDate, API_DATE_FORMAT).format(shortDateMonthFormat);

      values = {
        adminName,
        employeeName,
        dates
      };
      text = 'ContractTypes_approveTimeOffRequest_auditLog';
      break;
    }
    case 'AdminApprovedSickLeaveRequest': {
      const dates =
        amount > 1
          ? `${moment(startDate, API_DATE_FORMAT).format(shortDateMonthFormat)} - ${moment(
              endDate,
              API_DATE_FORMAT
            ).format(shortDateMonthFormat)}`
          : moment(startDate, API_DATE_FORMAT).format(shortDateMonthFormat);

      values = {
        adminName,
        employeeName,
        dates
      };
      text = 'ContractTypes_approveSickDayRequest_auditLog';
      break;
    }

    case 'LeaveContractCreated': {
      values = { adminName, contractName, leaveCount };
      text = 'audit_log_LeaveContractCreated';
      break;
    }

    case 'LeaveContractUpdated': {
      values = { adminName, contractName: contractName ?? '' };
      text = 'audit_log_LeaveContractUpdated';
      break;
    }

    case 'LeaveContractDeleted': {
      values = { adminName, contractName: contractName ?? '', affectedEmployeeCount: affectedEmployeeCount ?? 0 };
      text = 'audit_log_LeaveContractDeleted';
      break;
    }

    case 'EmployeeAssignedLeaveContract': {
      values = { adminName, employeeName, contractName: contractName ?? '' };
      text = 'audit_log_EmployeeAssignedLeaveContract';
      break;
    }

    case 'EmployeeLeaveContractUpdated': {
      values = { adminName, employeeName, contractName: contractName ?? '' };
      text = 'audit_log_EmployeeLeaveContractUpdated';
      break;
    }

    case 'EmployeeLeaveContractRemoved': {
      values = { adminName, employeeName, contractName: contractName ?? '' };
      text = 'audit_log_EmployeeLeaveContractRemoved';
      break;
    }

    case 'EmployeeLeaveContractTerminated': {
      const formattedEndDate = endDate ? moment(endDate, API_DATE_FORMAT).format(shortDateMonthFormat) : '';
      values = {
        adminName,
        employeeName,
        contractName: contractName ?? '',
        endDate: formattedEndDate,
        payoutSuffix: payoutRemainder ? t('audit_log_LeaveContract_payoutSuffix') : ''
      };
      text = 'audit_log_EmployeeLeaveContractTerminated';
      break;
    }

    case 'EmployeeLeaveContractChanged': {
      values = {
        adminName,
        employeeName,
        oldContractName: oldContractName ?? '',
        newContractName: newContractName ?? ''
      };
      text = 'audit_log_EmployeeLeaveContractChanged';
      break;
    }

    case 'EmployeeLeaveYearReset': {
      values = { adminName, employeeName, leaveName: leaveName ?? '', resetAmount };
      text = 'audit_log_EmployeeLeaveYearReset';
      break;
    }

    case 'EmployeeLeaveBalancesPaidOut': {
      values = {
        adminName,
        employeeName,
        contractName: contractName ?? '',
        leavesPayedOut,
        totalAmount: `${currency ?? ''}${totalAmount ?? 0}`
      };
      text = 'audit_log_EmployeeLeaveBalancesPaidOut';
      break;
    }

    case 'EmployeeLeaveAllowanceAdjusted': {
      values = { adminName, employeeName, leaveName: leaveName ?? '', amount, unit };
      text = 'audit_log_EmployeeLeaveAllowanceAdjusted';
      break;
    }

    case 'AdminAddedEmployeeLeave': {
      values = {
        adminName,
        employeeName,
        leaveName: leaveName ?? '',
        recordCount: recordCount ?? 0
      };
      text = 'audit_log_AdminAddedEmployeeLeave';
      break;
    }

    case 'AdminDeletedEmployeeLeave': {
      values = { adminName, employeeName, leaveName: leaveName ?? '' };
      text = 'audit_log_AdminDeletedEmployeeLeave';
      break;
    }

    case 'EmployeeLeaveSpecialDayAccrued': {
      const formattedHolidayDate = holidayDate ? moment(holidayDate, API_DATE_FORMAT).format(shortDateMonthFormat) : '';
      values = {
        employeeName,
        holidayName: holidayName ?? '',
        holidayDate: formattedHolidayDate,
        amount,
        unit
      };
      text = 'audit_log_EmployeeLeaveSpecialDayAccrued';
      break;
    }

    case 'EmployeeLeaveContractForfeitOnTermination': {
      values = {
        employeeName,
        leaveName: leaveName ?? '',
        balanceAtTermination: balanceAtTermination ?? 0
      };
      text = 'audit_log_EmployeeLeaveContractForfeitOnTermination';
      break;
    }

    case 'AdminUnbookedShiftAsLeave': {
      values = { adminName, employeeName, leaveName: leaveName ?? '' };
      text = 'audit_log_AdminUnbookedShiftAsLeave';
      break;
    }

    case 'EmployeeWeeklyPreferencesUpdated': {
      values = {
        employeeName,
        date: moment(date, API_DATE_FORMAT).format('dddd, D MMM YYYY'),
        startTime,
        endTime
      };
      text = WEEKLY_PREFERENCE_TEXT_BY_ACTION[action];
      break;
    }

    case 'AssignedEmployeeAsEventCaptain':
    case 'RemovedEmployeeAsEventCaptain': {
      values = { adminName, employeeName, eventName: captainEventName, dateRange };
      text = type;
      break;
    }

    case 'AssignedEmployeeSingleArea':
    case 'RemovedEmployeeSingleArea':
    case 'AssignedEmployeeMultipleAreas':
    case 'RemovedEmployeeMultipleAreas': {
      const multiple = type === 'AssignedEmployeeMultipleAreas' || type === 'RemovedEmployeeMultipleAreas';
      const sharedParams = { adminName, employeeName, eventName: captainEventName, dateRange };
      values = multiple
        ? { ...sharedParams, areaNames: captainAuditLabel(areaNames) }
        : { ...sharedParams, areaName: captainAuditLabel(areaName) };
      text = type;
      break;
    }

    case 'AssignedEmployeeShiftCaptain':
    case 'RemovedEmployeeShiftCaptain': {
      const positionLabel = positionSlug ? t(positionSlug) : '';
      const shiftStartTimeDisplay = shiftStartTime && moment.utc(shiftStartTime, 'HH:mm').format(timeFormat);
      const shiftEndTimeDisplay = shiftEndTime && moment.utc(shiftEndTime, 'HH:mm').format(timeFormat);
      values = {
        adminName,
        employeeName,
        eventName: captainEventName,
        dateRange,
        position: positionLabel,
        area: captainAuditLabel(captainAreaName),
        shiftStartTime: shiftStartTimeDisplay,
        shiftEndTime: shiftEndTimeDisplay
      };
      text = type;
      break;
    }

    case 'AssignedEmployeeSingleTask':
    case 'RemovedEmployeeSingleTask':
    case 'AssignedEmployeeMultipleTasks':
    case 'RemovedEmployeeMultipleTasks': {
      const multipleTasks = type === 'AssignedEmployeeMultipleTasks' || type === 'RemovedEmployeeMultipleTasks';
      const sharedParams = { adminName, employeeName, eventName: captainEventName, dateRange };
      values = multipleTasks
        ? { ...sharedParams, taskNames: captainAuditLabel(taskNames) }
        : { ...sharedParams, taskName: captainAuditLabel(taskName) };
      text = type;
      break;
    }

    case 'EmployeeCommentCreated':
      values = {
        adminName,
        employeeName
      };
      text = 'EmployeeCommentCreatedAuditLog';
      break;

    case 'EmployeeCommentDeleted':
      values = {
        adminName,
        employeeName
      };
      text = 'EmployeeCommentDeletedAuditLog';
      break;

    default:
      break;
  }

  return { text, values };
};

export const exportFormattedAuditLogs = ({
  t,
  auditLogs = [],
  dateFormat = 'DD/MM/YYYY',
  timeFormat = 'HH:mm',
  shortDateMonthFormat = 'DD/MM',
  title = 'auditLogs',
  getProfileFieldValue,
  currency,
  hasSensitiveDataAccess
}) => {
  const formattedData = auditLogs.reduce((prev, { type, data: payload, createdAt, ...rest }) => {
    const { text, values } = generateAuditLogBody({
      t,
      type,
      data: { type, data: JSON.parse(payload), createdAt, ...rest },
      timeFormat,
      dateFormat,
      shortDateMonthFormat,
      getProfileFieldValue,
      currency,
      hasSensitiveDataAccess
    });

    const details = (Object?.keys(values)?.length === 0 ? t(text) : t(text, values) || '')
      .replace(/<brs*[/]?>/gi, '\n')
      .replace(/<[^>]*>/gi, '');

    return [
      ...prev,
      {
        [t('createdAt')]: moment.utc(createdAt).local().format(`${dateFormat} ${timeFormat}`),
        [t('details')]: details
      }
    ];
  }, []);

  exportExcel({ baseData: formattedData, name: t(title) });
};
